import React, { useEffect, useRef, useState } from "react";
import "./ui.css";
import { STATUS_LABEL } from "../../lib/lab";

export const Page = ({ title, subtitle, actions, children }) => (
  <div className="lab-page">
    <div className="lab-page__head">
      <div>
        <h1 className="lab-page__title">{title}</h1>
        {subtitle && <div className="lab-page__sub">{subtitle}</div>}
      </div>
      {actions && <div className="lab-page__actions">{actions}</div>}
    </div>
    {children}
  </div>
);

export const Button = ({ variant = "secondary", size, className = "", children, loading, ...rest }) => (
  <button
    type="button"
    className={`lab-btn ${variant} ${size || ""} ${className}`}
    disabled={loading || rest.disabled}
    {...rest}
  >
    {loading ? <span className="lab-spinner" /> : children}
  </button>
);

export const Badge = ({ kind, children, className = "" }) => (
  <span className={`lab-badge ${kind || ""} ${className}`}>{children}</span>
);

export const StatusBadge = ({ status }) => <Badge kind={status}>{STATUS_LABEL[status] || status}</Badge>;

export const Field = ({ label, hint, error, children, className = "" }) => (
  <div className={`lab-field ${className}`}>
    {label && <label>{label}</label>}
    {children}
    {hint && !error && <span className="hint">{hint}</span>}
    {error && <span className="error">{error}</span>}
  </div>
);

export const Tabs = ({ tabs, value, onChange }) => (
  <div className="lab-tabs">
    {tabs.map((t) => (
      <button
        key={t.id}
        type="button"
        className={`lab-tab ${value === t.id ? "active" : ""}`}
        onClick={() => onChange(t.id)}
      >
        {t.label}
        {t.count != null && <span className="count">{t.count}</span>}
      </button>
    ))}
  </div>
);

export const Modal = ({ open, onClose, title, size = "", footer, children }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    // Modals stack (booking → result sheet → reason); Escape closes only the top one
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      const all = document.querySelectorAll(".lab-modal-bg");
      if (all[all.length - 1] === ref.current) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="lab-modal-bg" ref={ref} onMouseDown={onClose}>
      <div className={`lab-modal ${size}`} onMouseDown={(e) => e.stopPropagation()}>
        <div className="lab-modal__head">
          <h3>{title}</h3>
          <button type="button" className="lab-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="lab-modal__body">{children}</div>
        {footer && <div className="lab-modal__foot">{footer}</div>}
      </div>
    </div>
  );
};

// Small modal that collects a free-text reason (cancel / void / no-show)
export const ReasonModal = ({ open, title, label = "Reason", required = true, onSubmit, onClose, busy }) => {
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) setReason(""); }, [open]);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button onClick={onClose}>Back</Button>
          <Button variant="danger" loading={busy} disabled={required && !reason.trim()} onClick={() => onSubmit(reason.trim())}>
            Confirm
          </Button>
        </>
      }
    >
      <Field label={label}>
        <textarea className="lab-textarea w-full" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
      </Field>
    </Modal>
  );
};

export const Table = ({ columns, rows, rowKey = "_id", onRowClick, empty = "Nothing here yet", loading }) => (
  <div className="lab-table-wrap">
    <table className="lab-table">
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c.key} className={c.align === "right" ? "num" : ""} style={c.width ? { width: c.width } : undefined}>
              {c.title}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr><td colSpan={columns.length}><div className="lab-loading"><span className="lab-spinner" /> Loading…</div></td></tr>
        ) : rows.length === 0 ? (
          <tr><td colSpan={columns.length}><div className="lab-empty">{empty}</div></td></tr>
        ) : (
          rows.map((row) => (
            <tr key={row[rowKey]} className={onRowClick ? "clickable" : ""} onClick={onRowClick ? () => onRowClick(row) : undefined}>
              {columns.map((c) => (
                <td key={c.key} className={c.align === "right" ? "num" : ""}>
                  {c.render ? c.render(row) : row[c.key]}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

export const Pager = ({ page, totalPages, total, onChange }) => {
  if (!totalPages || totalPages <= 1) return total != null ? <div className="lab-pager">{total} total</div> : null;
  return (
    <div className="lab-pager">
      <span>{total != null ? `${total} total · ` : ""}Page {page} of {totalPages}</span>
      <Button size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>Prev</Button>
      <Button size="sm" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>Next</Button>
    </div>
  );
};

export const Stat = ({ label, value, sub, accent }) => (
  <div className={`lab-stat ${accent ? "accent" : ""}`}>
    <div className="label">{label}</div>
    <div className="value">{value}</div>
    {sub && <div className="sub">{sub}</div>}
  </div>
);

export const Loading = ({ text = "Loading…" }) => (
  <div className="lab-loading"><span className="lab-spinner" /> {text}</div>
);

export const Section = ({ title, right, children }) => (
  <div className="lab-section">
    {title && <h4>{title}{right && <span>{right}</span>}</h4>}
    {children}
  </div>
);

export const Bars = ({ items, color, format = (v) => v }) => {
  const max = Math.max(1, ...items.map((i) => Number(i.value) || 0));
  return (
    <div className="lab-bars">
      {items.map((i) => (
        <div className="lab-bar" key={i.label}>
          <span>{i.label}</span>
          <div className="track"><div className={`fill ${color || ""}`} style={{ width: `${((Number(i.value) || 0) / max) * 100}%` }} /></div>
          <span className="v">{format(i.value)}</span>
        </div>
      ))}
    </div>
  );
};
